import React, { useCallback, useEffect, useRef } from 'react';

const BTN = {
    padding: '3px 9px',
    border: '1px solid #fcd34d',
    background: 'white',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 800,
    color: '#92400e',
    minWidth: 28,
    lineHeight: 1.2,
};

function escapeHtml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/** Plain text → simple HTML for the editor (preserves line breaks). */
export function teacherCommentToHtml(text = '') {
    const raw = String(text || '');
    if (!raw.trim()) return '';
    if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
    return raw
        .split(/\n/)
        .map((line) => `<div>${line ? escapeHtml(line) : '<br>'}</div>`)
        .join('');
}

/** Strip to a small allowlist so teacher feedback can safely use dangerouslySetInnerHTML. */
export function sanitizeTeacherCommentHtml(html = '') {
    if (typeof document === 'undefined') {
        return String(html || '')
            .replace(/<script[\s\S]*?<\/script>/gi, '')
            .replace(/\son\w+="[^"]*"/gi, '')
            .replace(/\son\w+='[^']*'/gi, '');
    }
    const root = document.createElement('div');
    root.innerHTML = String(html || '');
    const allowed = new Set(['DIV', 'P', 'BR', 'STRONG', 'B', 'U', 'EM', 'I', 'SPAN']);
    const walk = (node) => {
        [...node.childNodes].forEach((child) => {
            if (child.nodeType === 1) {
                if (!allowed.has(child.tagName)) {
                    while (child.firstChild) node.insertBefore(child.firstChild, child);
                    node.removeChild(child);
                    return;
                }
                [...child.attributes].forEach((attr) => {
                    const name = attr.name.toLowerCase();
                    if (name.startsWith('on') || name === 'style' || name === 'href' || name === 'src') {
                        child.removeAttribute(attr.name);
                    }
                });
                walk(child);
            } else if (child.nodeType === 8) {
                node.removeChild(child);
            }
        });
    };
    walk(root);
    return root.innerHTML;
}

function exec(cmd) {
    document.execCommand('styleWithCSS', false, false);
    document.execCommand(cmd, false, null);
}

/**
 * Compact WYSIWYG for Teacher Comment / Feedback: Bold + Underline.
 * Value is HTML (existing plain-text comments are migrated on first edit).
 */
export default function TeacherCommentField({
    value = '',
    onChange,
    placeholder = "Write feedback for this student's work...",
    minHeight = 38,
}) {
    const editorRef = useRef(null);
    const focusedRef = useRef(false);
    const lastEmittedRef = useRef(value || '');

    useEffect(() => {
        const el = editorRef.current;
        if (!el || focusedRef.current) return;
        const next = teacherCommentToHtml(value || '');
        if (el.innerHTML !== next) el.innerHTML = next;
        lastEmittedRef.current = next;
    }, [value]);

    const emit = useCallback(() => {
        const html = sanitizeTeacherCommentHtml(editorRef.current?.innerHTML || '');
        lastEmittedRef.current = html;
        onChange?.(html);
    }, [onChange]);

    const run = (cmd) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        exec(cmd);
        emit();
    };

    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <button type="button" title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => run('bold')} style={BTN}>
                    <strong>B</strong>
                </button>
                <button type="button" title="Underline" onMouseDown={(e) => e.preventDefault()} onClick={() => run('underline')} style={BTN}>
                    <span style={{ textDecoration: 'underline' }}>U</span>
                </button>
            </div>
            <div
                ref={editorRef}
                contentEditable
                role="textbox"
                aria-multiline="true"
                data-placeholder={placeholder}
                onFocus={() => { focusedRef.current = true; }}
                onBlur={() => {
                    focusedRef.current = false;
                    emit();
                }}
                onInput={emit}
                style={{
                    minHeight,
                    border: '1.5px solid #fde68a',
                    borderRadius: 8,
                    padding: '0.45rem 0.65rem',
                    fontSize: '0.82rem',
                    lineHeight: 1.5,
                    fontFamily: 'inherit',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            />
            <style>{`
                [data-placeholder]:empty:before {
                    content: attr(data-placeholder);
                    color: #94a3b8;
                    pointer-events: none;
                }
            `}</style>
        </div>
    );
}
